import { ImageSettings } from '@/client/quizzes/types/quizzes.type';
import { FlexContainer } from '@/ui/styled-ui';
import { CogIcon } from '@/utils/helpers';
import { InputSwitch } from 'primereact/inputswitch';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const StyledImageSettingsContainer = styled(FlexContainer)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: var(--white-main);
  z-index: 1000;
  flex-direction: column;
  gap: var(--small-padding);
  width: 100%;
  padding: var(--default-padding);
  border-radius: var(--xsmall-border-radius);

  p {
    font-size: var(--medium-font-size);
    line-height: var(--medium-line-height);
    font-weight: 500;
    color: var(--black-main);
    margin: 0;
  }
`;

const CloseButton = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0;
  width: var(--small-button-height);
  height: var(--small-button-height);
  cursor: pointer;

  &:hover {
    i {
      color: var(--red-main);
    }
  }
`;

const StyledImageSettingsButton = styled(FlexContainer)`
  color: var(--black-main);
  font-weight: 500;
  font-size: var(--xsmall-font-size);
  line-height: var(--xsmall-line-height);
  cursor: pointer;
  gap: var(--xxsmall-padding);

  svg {
    transition: all 0.6s ease;
  }

  &:hover {
    svg {
      path {
        stroke: var(--red-main);
      }
      transform: rotate(180deg);
    }
  }

  i {
    font-size: var(--xsmall-font-size);
  }
`;

const StyledSettingsOption = styled(FlexContainer)`
  justify-content: flex-start;
  align-items: center;
  padding: 0;
  width: 100%;
  gap: var(--xsmall-padding);
`;

const StyledSettingsLabel = styled.label`
  font-size: var(--xsmall-font-size);
  line-height: var(--xsmall-line-height);
  font-weight: 500;
  color: var(--black-main);
  margin: 0;
`;

type ImageSettingsPanelProps = {
  imageSettings: ImageSettings;
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
  onBorderChange: (withBorder: boolean) => void;
  onZoomChange: (withZoom: boolean) => void;
};

export const ImageSettingsPanel: React.FC<ImageSettingsPanelProps> = ({
  imageSettings,
  isOpen,
  onOpen,
  onClose,
  onBorderChange,
  onZoomChange,
}) => {
  const { t } = useTranslation();

  if (isOpen) {
    return (
      <StyledImageSettingsContainer>
        <FlexContainer
          justify="space-between"
          align="center"
          style={{ marginBottom: 'var(--medium-padding)' }}
        >
          <p>{t('content.image.settings')}</p>
          <CloseButton onClick={onClose}>
            <i className="pi pi-times" />
          </CloseButton>
        </FlexContainer>
        <StyledSettingsOption>
          <InputSwitch
            className="v2"
            checked={imageSettings.withBorder || false}
            onChange={(e) => {
              onBorderChange(e.value);
            }}
          />
          <StyledSettingsLabel>
            {t('content.image.upload.add.border')}
          </StyledSettingsLabel>
        </StyledSettingsOption>
        <StyledSettingsOption>
          <InputSwitch
            className="v2"
            checked={imageSettings.withZoom || false}
            onChange={(e) => {
              onZoomChange(e.value);
            }}
          />
          <StyledSettingsLabel>
            {t('content.image.upload.add.zoom')}
          </StyledSettingsLabel>
        </StyledSettingsOption>
      </StyledImageSettingsContainer>
    );
  }

  return (
    <StyledImageSettingsButton onClick={onOpen}>
      <CogIcon />
      <span>{t('content.image.settings')}</span>
    </StyledImageSettingsButton>
  );
};
